<template>
  <gg-modal :is-active="modalStatus" @close="close">
    <template v-slot:toolbar>
      Edit Profile
    </template>
    <template v-slot:body>
      <v-row>
        <v-col cols="12" md="6" class="py-0">
          <gg-text-field
            v-model="userData.firstname"
            :error-messages="errors.collect('First Name')"
            label="First Name"
            required
            readonly
            disabled
            name="First Name"
            v-validate="'required'"
          />
        </v-col>
        <v-col cols="12" md="6" class="py-0">
          <gg-text-field
            v-model="userData.lastname"
            :error-messages="errors.collect('Last Name')"
            label="Last Name"
            required
            readonly
            disabled
            name="Last Name"
            v-validate="'required'"
          />
        </v-col>
        <v-col cols="12" md="6" class="py-0">
          <gg-text-field
            v-model="userData.email"
            :error-messages="errors.collect('Email')"
            label="Email"
            required
            disabled
            name="Email"
            v-validate="'required'"
          />
        </v-col>
        <v-col cols="12" md="6" class="py-0">
          <gg-text-field
            v-model="userData.companyname"
            :error-messages="errors.collect('Organization')"
            label="Organization"
            required
            name="Organization"
          />
        </v-col>
        <v-col cols="12" md="6" class="py-0">
          <gg-text-field
            v-model="userData.address1"
            :error-messages="errors.collect('Address 1')"
            label="Address 1"
            required
            name="Address 1"
            v-validate="'required'"
          />
        </v-col>
        <v-col cols="12" md="6" class="py-0">
          <gg-text-field
            v-model="userData.address2"
            :error-messages="errors.collect('Address 2')"
            label="Address 2"
            required
            name="Address 2"
          />
        </v-col>
        <v-col cols="12" md="4" class="py-0">
          <gg-text-field
            v-model="userData.city"
            :error-messages="errors.collect('City')"
            label="City"
            required
            name="City"
            v-validate="'required'"
          />
        </v-col>
        <v-col cols="12" md="4" class="py-0">
          <gg-text-field
            v-model="userData.state"
            :error-messages="errors.collect('State')"
            label="State"
            required
            name="State"
            v-validate="'required'"
          />
        </v-col>
        <v-col cols="12" md="4" class="py-0">
          <gg-text-field
            v-model="userData.postcode"
            :error-messages="errors.collect('Zip Code')"
            label="Zip Code"
            required
            name="Zip Code"
            v-validate="'required'"
          />
        </v-col>
        <v-col cols="12" md="5" class="py-0">
          <gg-input-label>Country</gg-input-label>
          <v-autocomplete
            outlined
            dense
            v-model="userData.country"
            :error-messages="errors.collect('Country')"
            :items="countries"
            item-text="name"
            item-value="code"
            name="Country"/>
        </v-col>
        <v-col cols="12" md="7" class="py-0">
          <v-row no-gutters>
            <v-col cols="12">
              <gg-input-label>Phone Number</gg-input-label>
            </v-col>
            <v-col cols="4" class="pr-5">
              <v-combobox
                outlined
                dense
                name="suffix"
                autocomplete="suffix"
                v-model="dialCode"
                auto-select-first
                :items="dialCodes"
              />
            </v-col>
            <v-col cols="8" class="d-flex flex-column">
              <gg-text-field
                class="mt-auto"
                v-model="phoneNumber"
                :error-messages="errors.collect('Phone Number')"
                required
                name="Phone"
                v-validate="'required'"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </template>
    <template v-slot:actions>
      <gg-btn secondary :disabled="loading" @click="close">
        Cancel
        <span class="visually-hidden text-lowercase">
            button
        </span>
      </gg-btn>
      <gg-btn primary @click="saveChanges" :loading="loading">
        Save Changes
        <span class="visually-hidden text-lowercase">
            button
        </span>
      </gg-btn>
    </template>
  </gg-modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { countries } from '@/helpers/countries'
import SpecialCharsMixin from '@/mixins/specialChars'

const dialCodes = countries.map(country => country.dial_code).sort((a, b) => a - b)

export default {
  name: 'EditProfileForm',
  mixins: [SpecialCharsMixin],
  props: {
    modalStatus: {
      type: Boolean,
      required: true
    },
  },
  data () {
    return {
      userData: {
        email: '',
        firstname: '',
        lastname: '',
        companyname: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        postcode: '',
        country: '',
        phonenumber: '',
      },
      dialCodes,
      loading: false,
    }
  },
  computed: {
    ...mapGetters(['SessionProfile']),
    countries () {
      return countries
    },
    dialCode: {
      get () {
        const splited = this.userData.phonenumber.split('.')
        return splited.length > 1 ? splited[0] : this.dialCodes[0]
      },
      set (val) {
        const splited = this.userData.phonenumber.split('.')
        this.userData.phonenumber = val + '.' + (splited.length > 1 ? splited[1] : '')
      }
    },
    phoneNumber: {
      get () {
        const splited = this.userData.phonenumber.split('.')
        return splited.length > 1 ? splited[1] : ''
      },
      set (val) {
        const splited = this.userData.phonenumber.split('.')
        this.userData.phonenumber = splited[0] + '.' + val
      }
    },
  },
  methods: {
    ...mapActions(['UpdateProfile', 'getProfile', 'reportSuccess', 'reportError']),
    setupFields () {
      // take initial values from store
      Object.keys(this.userData).map((key) => {
        this.userData[key] = this.convertChars(this.SessionProfile[key]) || ''
      })
    },
    close () {
      this.setupFields()
      this.$nextTick(() => {
        this.$validator.reset()
      })
      this.$emit('close')
    },
    async saveChanges () {
      try {
        const valid = await this.$validator.validateAll()
        if (!valid) return

        this.loading = true
        await this.UpdateProfile(this.userData)
        this.reportSuccess('Profile updated.')
        this.$emit('close')
        this.loading = false
      } catch (e) {
        this.loading = false
        this.reportError(e)
      }
    },
  },
  created () {
    this.setupFields()
  },
}
</script>
